import React, { createContext, FC, PropsWithChildren, useEffect, useMemo, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useSessionStorage } from "react-use";

import BackdropWithSpinner from "@/components/atoms/Loader/BackdropWithSpinner/BackdropWithSpinner";
import config from "@/config";
import { caseInsensitiveGet } from "@/core/object";
import { isNotEmpty } from "@/core/string";
import * as globalLogger from "@/globalLogger";
import { userApi } from "@/shared/apis";
import { anonymousUser, Identity, UserState } from "@/shared/domain";
import { GoneError, SCOPE_TOKEN_ALREADY_USED, SCOPE_TOKEN_EXPIRED, UnauthorizedError } from "@/shared/errors";
import {
    IDENTITY_STORAGE_KEY,
    isPreviewUser,
    parseIdentity,
    parseToken,
    pi2AuthTokenProvider,
    tokenProvider,
    TOPIC_ERROR_DIALOG,
    TOPIC_TOKEN_EXPIRED,
    useNavigation,
    usePubSub,
    useToast,
    xCorrelationIdProvider,
} from "@/shared/hooks";
import * as tagManager from "@/tagManager";

interface IAuthContext {
    identity: Identity;
    userState: UserState;
    logout?: () => void;
    login?: (token: string, email?: string, officeId?: string) => Promise<void>;
    loginScoped?: (context: string) => Promise<void>;
    init?: (userState: UserState) => void;
}

export const AuthContext = createContext<IAuthContext>({ identity: anonymousUser, userState: null });
AuthContext.displayName = "AuthContext";

const handleLogin = async (token: string, email: string, officeId: string): Promise<AuthValues> => {
    let accessRights = null;

    let userState = UserState.ACCESS;

    if (email) {
        const { state: receivedState } = await userApi.getUserStatus(email, officeId);
        userState = receivedState as UserState;
    }

    if (userState === UserState.ACCESS) {
        accessRights = await userApi.getAccessRights(token);
    }

    const identity = parseIdentity(token, accessRights, officeId);
    globalLogger.setIdentity(identity);

    tagManager.pushUserPropertyUserId(identity.id);
    tagManager.pushUserPropertyOfficeId(officeId);

    return { identity, userState };
};

export type AuthValues = { identity: Identity; userState: UserState };

type AuthProviderProps = {
    defaultAuthValues?: AuthValues;
};

export const AuthProvider: FC<PropsWithChildren<AuthProviderProps>> = ({ children, defaultAuthValues }) => {
    const [storedIdentity, storeIdentity] = useSessionStorage<Identity>(
        IDENTITY_STORAGE_KEY,
        defaultAuthValues?.identity || anonymousUser,
    );
    const [userState, setUserState] = useState<UserState>(defaultAuthValues?.userState || null);
    const [loading, setLoading] = useState(false);
    const identity = new Identity(storedIdentity);
    const { navToRoot } = useNavigation();
    const { subscribe, unsubscribe, publish } = usePubSub();
    const queryClient = useQueryClient();
    const { t, i18n } = useTranslation();
    const { errorToast } = useToast();

    const getAuthFromSession = async () => {
        setLoading(true);
        log.info("Auto login - identity");
        try {
            let newUserState = UserState.UNKNOWN;
            if (isPreviewUser()) {
                newUserState = UserState.ACCESS;
            } else if (identity.email) {
                const { state: receivedState } = await userApi.getUserStatus(identity.email, identity.officeId);
                newUserState = receivedState as UserState;
            }

            setUserState(newUserState);

            log.debug("Auto login Successful: ", identity);
        } finally {
            setLoading(false);
        }
    };

    // Context functions

    const login = async (token: string, email: string = null, officeId: string = null) => {
        tokenProvider.setToken(token);
        const data = await handleLogin(token, email, officeId);
        storeIdentity(data.identity);
        setUserState(data.userState);

        log.info("login successful", data);
    };

    const init = (newUserState: UserState) => {
        storeIdentity(anonymousUser);
        setUserState(newUserState);
    };

    const logout = () => {
        navToRoot(identity.officeId);
        queryClient.cancelQueries();
        queryClient.cancelMutations();
        sessionStorage.clear();
        xCorrelationIdProvider.resetXCorrelationId();
        tokenProvider.clearToken();
        pi2AuthTokenProvider.clearToken();
        globalLogger.setIdentity(anonymousUser);
        tagManager.pushUserPropertyUserId();
        tagManager.pushUserPropertyOfficeId();
        queryClient.clear();
        storeIdentity(anonymousUser);
        setUserState(UserState.UNKNOWN);
    };

    const tokenExpired = () => {
        logout();
        errorToast({ message: t("Toast.SessionExpired") });
    };

    const loginScoped = async (context: string) => {
        setLoading(true);
        try {
            const jwtToken = await userApi.loginScoped(context);
            const parsedToken = parseToken(jwtToken.token);
            const pi2Claims = caseInsensitiveGet(parsedToken, "pi2.claims", null);
            const language = caseInsensitiveGet(pi2Claims, "language", "");
            if (isNotEmpty(language) && config.AVAILABLE_LANGUAGES.includes(language)) {
                await i18n.changeLanguage(language);
            }
            await login(jwtToken.token);
        } catch (err) {
            if (err instanceof UnauthorizedError) {
                publish(TOPIC_ERROR_DIALOG, {
                    errorCode: SCOPE_TOKEN_EXPIRED,
                    redirectToBase: true,
                });
            } else if (err instanceof GoneError) {
                publish(TOPIC_ERROR_DIALOG, {
                    errorCode: SCOPE_TOKEN_ALREADY_USED,
                    redirectToBase: true,
                });
            } else {
                publish(TOPIC_ERROR_DIALOG, {
                    redirectToBase: true,
                });
            }
            setUserState(UserState.UNKNOWN);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        subscribe(TOPIC_TOKEN_EXPIRED, tokenExpired);
        return () => unsubscribe(TOPIC_TOKEN_EXPIRED);
    }, []);

    useEffect(() => {
        if (!userState && identity.isAuthenticated) {
            getAuthFromSession();
        }

        if (!identity.isAuthenticated) {
            const searchParams = new URL(window.location.href).searchParams;
            const scope = searchParams.get("sc");
            if (scope) {
                loginScoped(scope);
            } else {
                setUserState(UserState.UNKNOWN);
            }
        }
    }, []);

    const contextValue = useMemo(
        (): IAuthContext => ({
            logout,
            login,
            loginScoped,
            init,
            identity,
            userState,
        }),
        [storedIdentity, userState],
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {loading && <BackdropWithSpinner />}
            {userState && <>{children}</>}
        </AuthContext.Provider>
    );
};
